import { ApolloError, useMutation } from '@apollo/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CREATE_FISCAL_PROFILE_MUTATION,
  UPDATE_FISCAL_PROFILE_MUTATION,
} from './invoicing.gql';
import type { FiscalProfile } from './types';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface FiscalProfileFormDialogProps {
  customer_id: string;
  profile: FiscalProfile | null;
  on_close: () => void;
  on_done: () => void | Promise<void>;
  on_error: (msg: string | null) => void;
}

export function FiscalProfileFormDialog({
  customer_id,
  profile,
  on_close,
  on_done,
  on_error,
}: FiscalProfileFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const is_edit = profile !== null;

  const [label, setLabel] = useState(profile?.label ?? '');
  const [rfc, setRfc] = useState(profile?.rfc ?? '');
  const [legal_name, setLegalName] = useState(profile?.legal_name ?? '');
  const [tax_regime_code, setTaxRegimeCode] = useState(profile?.tax_regime_code ?? '601');
  const [postal_code, setPostalCode] = useState(profile?.postal_code ?? '');
  const [cfdi_usage, setCfdiUsage] = useState(profile?.cfdi_usage ?? 'G03');
  const [email, setEmail] = useState(profile?.email ?? '');
  const [is_default, setIsDefault] = useState<boolean>(profile?.is_default ?? false);

  const [createProfile, { loading: c_loading }] = useMutation(CREATE_FISCAL_PROFILE_MUTATION);
  const [updateProfile, { loading: u_loading }] = useMutation(UPDATE_FISCAL_PROFILE_MUTATION);
  const loading = c_loading || u_loading;

  const can_submit =
    !loading &&
    rfc.trim().length >= 12 &&
    legal_name.trim().length > 0 &&
    tax_regime_code.trim().length > 0 &&
    /^\d{5}$/.test(postal_code.trim()) &&
    cfdi_usage.trim().length > 0;

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    on_error(null);
    try {
      if (is_edit && profile) {
        await updateProfile({
          variables: {
            input: {
              id: profile.id,
              label: label.trim() || null,
              rfc: rfc.trim().toUpperCase(),
              legal_name: legal_name.trim(),
              tax_regime_code: tax_regime_code.trim(),
              postal_code: postal_code.trim(),
              cfdi_usage: cfdi_usage.trim().toUpperCase(),
              email: email.trim().toLowerCase() || null,
              is_default,
            },
          },
        });
      } else {
        await createProfile({
          variables: {
            input: {
              customer_id,
              label: label.trim() || null,
              rfc: rfc.trim().toUpperCase(),
              legal_name: legal_name.trim(),
              tax_regime_code: tax_regime_code.trim(),
              postal_code: postal_code.trim(),
              cfdi_usage: cfdi_usage.trim().toUpperCase(),
              email: email.trim().toLowerCase() || null,
              is_default,
            },
          },
        });
      }
      await on_done();
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code === 'rfc_invalid') return on_error(t('invoicing.error_rfc_invalid'));
        if (code === 'postal_code_invalid')
          return on_error(t('invoicing.error_postal_code_invalid'));
        return on_error(code);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="font-display tracking-tight">
            {is_edit ? t('invoicing.fiscal_profile_edit') : t('invoicing.fiscal_profile_create')}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1 md:col-span-2">
            <Label>{t('invoicing.field_label')}</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              disabled={loading}
              placeholder={t('invoicing.field_label_placeholder')}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_rfc')} *</Label>
            <Input
              value={rfc}
              onChange={(e) => setRfc(e.target.value.toUpperCase())}
              disabled={loading}
              maxLength={13}
              className="font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_postal_code')} *</Label>
            <Input
              value={postal_code}
              onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              disabled={loading}
              inputMode="numeric"
              className="font-mono"
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>{t('invoicing.field_legal_name')} *</Label>
            <Input
              value={legal_name}
              onChange={(e) => setLegalName(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_tax_regime')} *</Label>
            <Input
              value={tax_regime_code}
              onChange={(e) => setTaxRegimeCode(e.target.value.replace(/\D/g, ''))}
              disabled={loading}
              className="font-mono"
              maxLength={4}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('invoicing.field_cfdi_usage')} *</Label>
            <Input
              value={cfdi_usage}
              onChange={(e) => setCfdiUsage(e.target.value.toUpperCase())}
              disabled={loading}
              className="font-mono"
              maxLength={6}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label>{t('invoicing.field_email')}</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>
          <label className="flex items-center gap-2 text-sm md:col-span-2">
            <input
              type="checkbox"
              checked={is_default}
              onChange={(e) => setIsDefault(e.target.checked)}
              disabled={loading}
            />
            <span>{t('invoicing.field_set_default')}</span>
          </label>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('common.save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
